import type { TemplatePack } from './interpretation.js';

export type OutputSafetyIssueCode =
  | 'forbidden_guarantee_language'
  | 'unresolved_evidence_ref'
  | 'missing_rule_provenance';

export interface OutputSafetyIssue {
  code: OutputSafetyIssueCode;
  path: string;
  message: string;
}

interface AuditableClaim {
  claimKey: string;
  text: string;
  evidenceRefs: readonly string[];
  ruleProvenance: readonly { sourceLocator: string; normalizedRationale: string }[];
}

interface AuditableTopic {
  topic: string;
  claims: readonly AuditableClaim[];
}

interface AuditableReport {
  topics: readonly AuditableTopic[];
}

const FORBIDDEN_OUTPUT_PATTERNS: readonly { pattern: RegExp; label: string }[] = Object.freeze([
  { pattern: /保證/u, label: '保證式語言' },
  { pattern: /必定死亡|一定死亡/u, label: '死亡確定性斷語' },
  { pattern: /一定發財|穩賺/u, label: '財務保證式斷語' },
  { pattern: /必富必貴/u, label: '富貴確定性斷語' },
  { pattern: /官階/u, label: '古代身分結果判語' },
]);

export function lintSafeOutputText(text: string, path = 'text'): readonly OutputSafetyIssue[] {
  const issues: OutputSafetyIssue[] = [];
  for (const entry of FORBIDDEN_OUTPUT_PATTERNS) {
    if (entry.pattern.test(text)) {
      issues.push({
        code: 'forbidden_guarantee_language',
        path,
        message: `${path} contains ${entry.label}`,
      });
    }
  }
  return issues;
}

export function lintTemplatePackSafety(templatePack: TemplatePack): readonly OutputSafetyIssue[] {
  return Object.entries(templatePack.templates).flatMap(([claimKey, template]) =>
    template.variants.flatMap((variant, index) => lintSafeOutputText(variant, `template.${claimKey}.variants[${index}]`)),
  );
}

function generatedTopics(report: AuditableReport): readonly AuditableTopic[] {
  return report.topics;
}

export function auditGeneratedReportSafety(
  report: AuditableReport,
  knownEvidenceRefs?: ReadonlySet<string>,
): readonly OutputSafetyIssue[] {
  const issues: OutputSafetyIssue[] = [];
  for (const topic of generatedTopics(report)) {
    for (const claim of topic.claims) {
      issues.push(...lintSafeOutputText(claim.text, `topic.${topic.topic}.claim.${claim.claimKey}.text`));
      if (claim.ruleProvenance.some((entry) => !entry.sourceLocator.trim() || !entry.normalizedRationale.trim())) {
        issues.push({
          code: 'missing_rule_provenance',
          path: `topic.${topic.topic}.claim.${claim.claimKey}.ruleProvenance`,
          message: `Claim ${claim.claimKey} has incomplete rule provenance`,
        });
      }
      if (knownEvidenceRefs) {
        for (const evidenceRef of claim.evidenceRefs) {
          if (!knownEvidenceRefs.has(evidenceRef)) {
            issues.push({
              code: 'unresolved_evidence_ref',
              path: `topic.${topic.topic}.claim.${claim.claimKey}.evidenceRefs`,
              message: `Claim ${claim.claimKey} references unknown evidence ${evidenceRef}`,
            });
          }
        }
      }
    }
  }
  return issues;
}

export function assertGeneratedReportSafety(
  report: AuditableReport,
  knownEvidenceRefs?: ReadonlySet<string>,
): void {
  const issues = auditGeneratedReportSafety(report, knownEvidenceRefs);
  if (issues.length) throw new Error(`Unsafe generated report: ${issues.map((issue) => issue.message).join('; ')}`);
}
